/// <reference path="Utilities/_reference.ts" />
/// <reference path="../../../node_modules/@types/leaflet/index.d.ts" />

/**
 * Mapping namespace
 * @namespace
 */
module YVR.StaticMap {
	// Dependencies
	import logger = YVR.Utilities.Logger;
	import util = YVR.Utilities.Map;

	// Utilities
	const cache: { [key: string]: any; } = {};
	const config = $.extend({}, util.config);
	const layers = {};
	const state: util.IMapState = config.initialState;

	let increment = 1;
	let maps = [];


	/**
	 * Initialise the entire mapping component.
	 */
	export function init() {
		const elements = $('.yvr-map--mini:visible');

		if (elements.length) {
			logger.log(`[static-map][init] Found ${elements.length} map(s).`);

			getData().done(() => {
				elements.each(function (element, index) {
					const container = $(this);

					let id = container.attr('id');
					const lat = parseFloat(container.data('maps-marker-lat'));
					const lng = parseFloat(container.data('maps-marker-lng'));
					const poiId = parseInt(container.data('maps-poiid'), 10);
					const poiCategory = container.data('maps-poicategory');
					const url = container.data('maps-url');
					const level = parseInt(container.data('maps-level'), 10);
					const floors = container.data('maps-levels');
					const terminal = container.data('maps-terminal');
					const image = container.data('maps-storefront');
					const showNonPassengerAreas = container.data('maps-show-non-passenger-areas') !== undefined;

					container.find('.yvr-map-label__name').text(floors[level]);

					// Replace the ID of the container due to an issue with Leaflet
					// where the remove() function doesn't always remove previous
					// maps.
					id += `-${increment++}`;
					container.attr('id', id);

					// Check to see if the element still exists
					if ($(`#${id}`).length) {
						logger.log(`[static-map][init] Creating map with id: ${id}`);

						const map = createMap(id, container, lat, lng);

						// Add `notranslate` classes to the map pane.
						$(`#${id}`).find('.leaflet-pane').addClass('notranslate');

						renderFloorLevel(map, level, terminal, showNonPassengerAreas);
						renderMarker(map, image, poiId, poiCategory, terminal, url, lat, lng);

						maps.push(map);
					}
				});
			});
		}
	}


	/**
	 * Destory all the maps on the page.
	 */
	export function removeAll() {
		for (let i = 0; i < maps.length; i++) {
			maps[i].remove();
		}

		maps = [];
	}


	/**
	 * Create a Leaflet map.
	 * @return {Map} A Leaflet map object.
	 */
	function createMap (id, container, lat, lng) {
		return L.map(id, {
			attributionControl: false,
			center: [lat, lng],
			closePopupOnClick: false,
			boxZoom: false,
			doubleClickZoom: false,
			dragging: false,
			keyboard: false,
			scrollWheelZoom: false,
			tap: false,
			touchZoom: false,
			zoom: 16.1,
			maxZoom: 16.1,

			// Turn off the default zoom control.
			zoomControl: false
		});
	}


	/**
	 * Get the room data and return a promise.
	 */
	function getData () {
		const rooms = config.urls.rooms;

		return $.when(
			$.getJSON(rooms.BaseLayer, data => {
				cache[rooms.BaseLayer] = data;
			}),
			$.getJSON(rooms[1], data => {
				cache[rooms[1]] = data;
			}),
			$.getJSON(rooms[2], data => {
				cache[rooms[2]] = data;
			}),
			$.getJSON(rooms[3], data => {
				cache[rooms[3]] = data;
			}),
			$.getJSON(rooms[4], data => {
				cache[rooms[4]] = data;
			}),
			$.getJSON(rooms.Roads, data => {
				cache[rooms.Roads] = data;
			}),
			$.getJSON(rooms.ParkingLots, data => {
				cache[rooms.ParkingLots] = data;
			}),
			$.getJSON(`${config.urls.gates.service}?$select=${config.urls.gates.select.join(',')}`, data => {
				cache['gates'] = data.value;
			})
		);
	}


	/**
	 * Render the level.
	 * @param {Map} map A Leaflet map object.
	 */
	function renderFloorLevel(map: L.Map, level: number, terminal: string, showNonPassengerAreas: boolean) {
		addBaseLevelLayer(map, cache[config.urls.rooms.BaseLayer], showNonPassengerAreas);
		addParkingLevelLayer(map, cache[config.urls.rooms.ParkingLots]);
		addRoadsLevelLayer(map, cache[config.urls.rooms.Roads]);

		addFloorLevelLayer(map, cache[config.urls.rooms[level]], level, terminal, showNonPassengerAreas);
		addGateLayer(map);
	}


	/**
	 * Adds a base layer of a level on our map with the given GeoJSON data.
	 * @param {Map}    map  The Leaflet map instance.
	 * @param {Object} data GeoJSON data.
	 */
	function addBaseLevelLayer(map: L.Map, data: Object, showNonPassengerAreas: boolean): void {
		(L as any).geoJSON(data, {
			interactive: false,
			fillOpacity: 1,
			keyboard: false,
			style(feature) {
				return {
					color: util.getFeatureColor(feature, state.terminals, { showNonPassengerAreas })
				};
			},
			opacity: 1
		}).addTo(map);
	}


	/**
	 * Adds a base parking lot layer of a level on our map with the given GeoJSON data.
	 * @param {Map}    map  The Leaflet map instance.
	 * @param {Object} data GeoJSON data.
	 */
	function addParkingLevelLayer(map: L.Map, data: Object): void {
		(L as any).geoJSON(data, {
			interactive: false,
			fillOpacity: 1,
			keyboard: false,
			style(feature) {
				return {
					color: util.config.colors.road
				};
			},
			opacity: 1
		}).addTo(map);
	}


	/**
	 * Adds a base layer of a level on our map with the given GeoJSON data.
	 * @param {Map}    map  The Leaflet map instance.
	 * @param {Object} data GeoJSON data.
	 */
	function addRoadsLevelLayer(map: L.Map, data: Object): void {
		(L as any).geoJSON(data, {
			interactive: false,
			fillOpacity: 1,
			keyboard: false,
			style(feature) {
				return {
					color: util.config.colors.road,
					lineCap: 'butt',
					weight: 5
				};
			},
			opacity: 1
		}).addTo(map);
	}


	/**
	 * Adds a base layer of a level on our map with the given GeoJSON data.
	 * @param {Map}    map              The Leaflet map instance.
	 * @param {Object} data             GeoJSON data.
	 * @param {Number} floorLevelNumber The level number of the GeoJSON data.
	 * @param {String} terminal         The temrinal that the current marker is in.
	 */
	function addFloorLevelLayer(map: L.Map, data: Object, floorLevelNumber: number, terminal: string, showNonPassengerAreas: boolean) {
		state.terminals = util.config.terminalMapping[terminal];

		layers[floorLevelNumber] = (L as any).geoJSON(data, {
			interactive: false,
			fillOpacity: 1,
			filter(feature) {
				// Filter out any sectors not necessary for the map, such as the parkade.
				return !config.excludedSectors.includes(feature.properties.Sector) &&
					floorLevelNumber === parseInt(feature.properties.FloorLevel, 10);
			},
			keyboard: false,
			style(feature) {
				return {
					color: util.getFeatureColor(feature, state.terminals, { showNonPassengerAreas })
				};
			},
			opacity: 1
		}).addTo(map);
	}


	/**
	 * Create a Marker Cluster Group layer using a set of default properties;
	 * @param props The properties to pass into the marker cluster constructor.
	 */
	function createIconMarkerClusterGroup(props: Object): L.LayerGroup {
		const defaults = {
			showCoverageOnHover: false,
			spiderfyOnMaxZoom: false,
			zoomToBoundsOnClick: false
		};

		const properties = $.extend({}, defaults, props);

		return (L as any).markerClusterGroup(properties);
	}


	/**
	 * Adds the gate labels to a map layer.
	 * @param {Map} map The Leaflet map instance.
	 */
	function addGateLayer(map) {
		// Create a marker cluster group since elevators are common in group
		const layer = createIconMarkerClusterGroup({
			disableClusteringAtZoom: 19,
			iconCreateFunction(cluster) {
				return util.createGateClusterIcon(cluster);
			},
			maxClusterRadius: 25
		});

		for (const feature of cache['gates']) {
			const latlng = L.latLng(feature.Latitude, feature.Longitude);
			const icon = util.createGateIcon(feature);
			const marker = L.marker(latlng, {
				// Use a custom `divIcon` so we remove all styles except for the text
				icon,
				interactive: false,
				keyboard: false
			});

			layer.addLayer(marker);
		}

		layer.addTo(map);
	}


	/**
	 * Render an individual marker.
	 * @param {Map}    map   A Leaflet map.
	 * @param {String} image A URL to an image used for the popup.
	 * @param {Number} poiId The unique ID for a facility.
	 * @param {String} poiCategory The category of the facility.
	 * @param {String} url   The URL to the terminal map.
	 * @param {Float}  lat   The latitude of the marker to add to the map.
	 * @param {Float}  lng   The longitude of the marker to add to the map.
	 */
	function renderMarker(map, image, poiId: number, poiCategory: string, terminal: string, url, lat, lng) {
		const latlng = L.latLng(lat, lng);

		const marker = L.marker(latlng, {
			draggable: false,
			interactive: false,
			keyboard: false,
			icon: util.createSelectedIcon()
		});

		if (image) {
			// Create a popup with our location data from the gateway.'
			let html = '';

			if (url && poiId > -1) {
				const terminalSlug = terminal.toLowerCase().trim().replace(/\s/, '-');
				html += `<a href="${url}#terminal=${terminalSlug}&facility=${poiId}&locations=${poiCategory}">`;
			}

			html += `<img class="yvr-map-popup__storefront" src="${image}">`;

			if (url && poiId > -1) {
				html += '</a>';
			}

			const popup = L.popup(config.facilityPopup).setContent(html);

			marker.bindPopup(popup);
		}

		marker.addTo(map);

		if (image) {
			marker.openPopup();
		}
	}


	// Kick it
	init();
}
